/*       */

// $FlowFixMe
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { set } from 'app/utils/lo/lo';

export const usePageActive = (callback) => {
    const isChatClosed = useSelector(state => state.chat.socket.isClosed);
    const isChatFailed = useSelector(state => state.chat.socket.isFailed);
    const isChatClosedRef = useRef(isChatClosed);
    const isChatFailedRef = useRef(isChatFailed);
    
    useEffect(() => {
        isChatFailedRef.current = isChatFailed;
    }, [isChatFailed]);
    
    useEffect(() => {
        isChatClosedRef.current = isChatClosed;
    }, [isChatClosed]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && (isChatFailedRef.current || isChatClosedRef.current)) {
                callback();
            }
        };
        const handleOnline = () => {
            if (isChatFailedRef.current || isChatClosedRef.current) {
                callback();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnline);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
        };
    }, [callback]);
};

export const usePrevious = value => {
    const ref = useRef(value);
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

export const useToggle = (defaultState = false) => {
    const [isOpen, show] = useState(defaultState);
    const toggle = useCallback(
        e => {
            if (e && e.stopPropagation) {
                e.stopPropagation();
                e.preventDefault();
            }
            show(!isOpen);
        },
        [isOpen]
    );
    return [isOpen, toggle, show];
};

/**
 * Use this hook to handle the variable changes providing an additional onChange function.
 *
 * @param value the property value
 * @param refresh if "true" it refreshes the value on every change, otherwise it set the value only the first time. This flag shouldn't be modified during the hook life cycle!
 *
 * @return three items: the value, the onChange function, the set function.
 */
export const useOnChange = (value, refresh = true) => {
    const [val, setValue] = useState(value);
    refresh &&
        useEffect(() => { // eslint-disable-line react-hooks/rules-of-hooks
            setValue(value);
        }, [value]);
    const onChange = useCallback(
        e => {
            setValue(e.target.value);
        },
        [setValue]
    );
    return [val, onChange, setValue];
};

/**
 * Use a Redux action.
 *
 * @param action the action (required). If the action is asyncronous it must return a Promise.
 * @param paramters an array containing the action parameters (optional).
 * @param disableUI the function to disable the UI (optional).
 * @param onSuccess the function to call on success (optional).
 * @param onError the function to call on error (optional).
 */
export const useReduxAction = ({ action, parameters, disableUI, onSuccess, onError }) =>
    useCallback(() => {
        disableUI && disableUI(true);
        const result = parameters ? action(...parameters) : action();
        const promise = result instanceof Promise ? result : Promise.resolve(result);
        return promise.then(res => {
            disableUI && disableUI(false);
            if (res instanceof Error) {
                onError && onError(res);
            } else {
                onSuccess && onSuccess(res);
            }
            return res;
        });
    }, [parameters, action, disableUI, onError, onSuccess]);

export const useForm = (initialState, onSubmit) => {
    // $FlowFixMe
    const [form, setForm] = useState(initialState);

    const handleChange = useCallback(event => {
        if (event.persist) {
            event.persist();
        }
        setForm(form => set({ ...form }, event.target.name, event.target.type === 'checkbox' ? event.target.checked : event.target.value));
    }, []);

    const resetForm = useCallback(() => {
        setForm(initialState);
    }, [initialState]);

    const setInForm = useCallback((name, value) => {
        setForm(form => set(form, name, value));
    }, []);

    const handleSubmit = useCallback(
        event => {
            if (event) {
                event.preventDefault();
            }
            if (onSubmit) {
                onSubmit();
            }
        },
        [onSubmit]
    );

    return {
        form,
        handleChange,
        handleSubmit,
        resetForm,
        setForm,
        setInForm,
    };
};

export const useOnScreen = ref => {
    const [isIntersecting, setIntersecting] = useState(false);

    const observer = useMemo(() => new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting)), []);

    useEffect(() => {
        observer.observe(ref.current);
        // Remove the observer as soon as the component is unmounted
        return () => {
            observer.disconnect();
        };
    }, [observer, ref]);

    return isIntersecting;
};

export const useImageExists = (imageUrl) => {
    const [imageExists, setImageExists] = useState(false);

    const checkImage = useCallback(async (imageUrl) => {
        try {
            const response = await fetch(imageUrl, { method: 'HEAD' });
            if (response.ok) {
                setImageExists(true);
                return;
            } 
            setImageExists(false);
        } catch (error) {
            console.error('Error checking image:', error);
            setImageExists(false);
        }
    }, []);

    useEffect(() => {
        checkImage(imageUrl);
    }, [imageUrl, checkImage]);

    return imageExists;
}